<script setup>
  import { useSlots } from 'vue';

  const props = defineProps({
    color: {
      type: String,
      required: false,
      default: 'danger',
      validator(value) {
        return ['danger', 'success', 'information'].includes(value);
      },
    },
    // Drops the block's bottom margin so the alert sits flush inside a tight
    // container (e.g. a thread bubble) that owns its own spacing.
    marginless: {
      type: Boolean,
      required: false,
      default: false,
    },
    // WHERE THE ALERT SITS in what surrounds it, which is what decides on which
    // side it needs air:
    //
    //   * `top`    - margin below (the default: an alert that opens a block)
    //   * `middle` - margin above and below
    //   * `bottom` - margin above, nothing below (an alert that closes a block)
    //
    // `marginless` is a different axis and WINS over this one: it means "no
    // margin at all", the more specific instruction ("the container owns the
    // spacing"), and it wins by construction - it redeclares the margin
    // shorthand these positions only write the edges of.
    //
    // Whatever the position, an alert IMMEDIATELY FOLLOWED BY ANOTHER shrinks
    // the space below it on its own (see the stylesheet): stacked alerts are one
    // block, and no caller has to say so.
    position: {
      type: String,
      required: false,
      default: 'top',
      validator(value) {
        return ['top', 'middle', 'bottom'].includes(value);
      },
    },
  });

  const classes = ['credere-alert', `color--${props.color}`, `position--${props.position}`]
    .concat(props.marginless ? ['variant--marginless'] : []);
  const slots = useSlots();
</script>

<template>
  <section :class="classes">
    <span class="credere-alert-icon">!</span>

    <div class="credere-alert-paragraph">
      <slot />
    </div>

    <!-- Third item of the alert's own row, beside the sentence and never under
         it, in a wrapper the alert owns rather than the caller's markup: every
         alert that offers an action puts it in the same place. That is what
         makes a slot enough here - the caller chooses WHICH button, never
         where it sits. -->
    <div
      v-if="slots.action"
      class="credere-alert-action"
    >
      <slot name="action" />
    </div>
  </section>
</template>

<style lang="scss">
  @import '@css/mixins/mobile.scss';

  .credere-alert {
    --background-color: var(--token-color-constant-dark);
    --border-color: var(--token-color-alert-danger);
    --border-style: solid;
    --border-width: 0 0 0 var(--token-spacing-xxxs);
    --border-radius: var(--token-border-radius-small);
    --color: var(--token-color-constant-light);
    --font-size: var(--token-font-size-m);
    --gap: var(--token-spacing-xxxs);
    --line-height: var(--token-line-height-l);
    // THE TWO EDGES, SEPARATELY, so a rule can move one without erasing the
    // other: `--margin` is a shorthand, and any block that redeclares it to
    // change the bottom also silently zeroes the top a `position` had put there.
    // Namespaced because the reset (`* { @include tokens-reset }`) owns the bare
    // token names and does NOT declare these - a bare `--margin-top` here would
    // inherit into every descendant of the alert.
    --alert-margin-top: 0;
    --alert-margin-bottom: var(--token-spacing-blaster);
    --margin: var(--alert-margin-top) 0 var(--alert-margin-bottom) 0;
    --padding: var(--token-spacing-xxs);

    align-items: flex-start;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;

    @include mobile-font-size;

    // `position--top` has no block on purpose: it IS the pair declared above, so
    // the default keeps behaving exactly as it did before the prop existed (the
    // same idiom Buttons uses for `spacing--none`).
    &.position--middle {
      --alert-margin-top: var(--token-spacing-blaster);
    }

    &.position--bottom {
      --alert-margin-top: var(--token-spacing-blaster);
      --alert-margin-bottom: 0;
    }

    // TWO ALERTS IN A ROW ARE ONE BLOCK, and the full margin between them reads
    // as two - the gate notices in Conexões com a Meta are the case that showed
    // it. It touches the BOTTOM EDGE ONLY, which is the whole reason the edges
    // are separate variables: written as a `--margin` this would also wipe the
    // top margin of a `position--middle` alert that happens to be followed by
    // another (Automations/Notifications stack exactly that pair).
    //
    // AFTER the positions on purpose, and they weigh the same (0,2,0), so source
    // order is what decides: it also overrides `position--bottom`'s zero, which
    // is right - "closes the block" stops being true the moment another alert
    // follows.
    //
    // It does NOT reach an alert whose caller set its own `--margin` (the Inbox
    // stacks three at `xxxs`): that declaration replaces the shorthand, so the
    // edge variables stop being read at all. Deliberate - a caller that spelled
    // out its spacing means it.
    &:has(+ .credere-alert) {
      --alert-margin-bottom: var(--token-spacing-s);
    }

    // Still last, but no longer BECAUSE of source order: "no margin at all"
    // redeclares the shorthand, so it wins over the edge variables by
    // construction - none of the blocks above can undo it anymore.
    &.variant--marginless {
      --margin: 0;
    }

    &.color--success {
      --border-color: var(--token-color-alert-success);
    }

    &.color--information {
      --border-color: var(--token-color-alert-information);
    }

    b {
      --font-weight: var(--token-font-weight-bold);
    }
  }

  .credere-alert-icon {
    --background-color: var(--token-color-alert-danger);
    --border-radius: var(--token-border-radius-circular);
    --color: var(--token-color-constant-dark);
    --font-size: var(--token-font-size-s);
    --font-weight: var(--token-font-weight-bold);
    --margin: 0.25rem 0 0;

    flex-shrink: 0;
    height: 1rem;
    line-height: 1rem;
    text-align: center;
    width: 1rem;

    .color--success & {
      --background-color: var(--token-color-alert-success);
    }

    .color--information & {
      --background-color: var(--token-color-alert-information);
    }
  }

  // Takes the row's slack so an action beside it is pushed to the far end.
  .credere-alert-paragraph {
    display: block;
    flex: 1;
  }

  // Beside the sentence, at the end of the row. The space between the two is the
  // row's own gap and nothing else - a margin here would stack on top of it.
  // `flex-shrink: 0` keeps the button at its own width while the text takes the
  // rest, and `align-self` centres it against a sentence that may wrap to
  // several lines: the row itself is `flex-start`, which would pin it to the
  // first line.
  .credere-alert-action {
    align-items: center;
    align-self: center;
    display: flex;
    flex-direction: row;
    flex-shrink: 0;
    justify-content: flex-end;
  }
</style>
